import React, { useState, useEffect, useRef, forwardRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import { THREE } from 'expo-three'
import { useApi } from '../../systems/Physics/hooks'
import { useEvents } from '../../systems/World/hooks'
import UfoShip from '../UfoShip'
import useOutOfTheBox from '../../hooks/useOutOfTheBox'
import animateFallUp from '../../helpers/animateFallUp'
import respawnMe from '../../helpers/respawnMe'
import { CHARACTER_EVENTS, GAME_EVENTS } from '../../systems/World/Events'
import useCollision from '../../hooks/useCollision'

const useTurnsCounter = (callback, ref) => {
  const flagSafeTurn = useRef(true);
  const [turns, setTurns] = useState(0)
  const [originalAngle, setOriginalAngle] = useState(0)
  const [active, setActive] = useState()
  useFrame(() => {
    if (!ref.current || !active) return
    if (~~ref.current.rotation.y === originalAngle) {
      if (flagSafeTurn.current) {
        setTurns(turns + 1)
        flagSafeTurn.current = false
      }
    } else {
      flagSafeTurn.current = true
    }
  })
  useEffect(() => {
    if (!ref.current || !active) return
    setOriginalAngle(~~ref.current.rotation.y)
  }, [ref.current])

  useEffect(() => {
    if(turns !== 0) {
      callback(turns)
    }
  }, [turns])

  useEvents(GAME_EVENTS.READY_ON, () => setActive(true))
  useEvents(GAME_EVENTS.READY_OFF, () => setActive(false))
  
  // useTimeout(() => setTurns(1), 1000)
  
  return null
}

const getAggressivenessByModel = model => model === 4 ? 0.8 : model === 3 ? 0.5 : model === 2 ? 0.3 : 0
const getWandernessByModel = model => model === 4 ? 0.9 : model === 3 ? 0.8 : model === 2 ? 0.7 : 0
const getReflexesByModel = model => model === 4 ? 1000 : model === 3 ? 2000 : model === 2 ? 3000 : 4000
const Enemy = forwardRef(({
  userController,
  enemies,
  model,
  spin,
  ...props
}, externalRef) => {
  
  const api = useApi()
  // const [attack, setAttack] = useState(false);
  const target = useRef()
  const impulse = useRef(200)
  const impulseRef = useRef()
  const impulseTID = useRef()
  const retargetTID = useRef()
  const outOfRing = useOutOfTheBox(externalRef)
  const triggerOutOfRing = useEvents(CHARACTER_EVENTS.OUT_OF_RING)
  const triggerImpulseOn = useEvents(CHARACTER_EVENTS.IMPULSE_ON)
  const triggerImpulseOff = useEvents(CHARACTER_EVENTS.IMPULSE_OFF)
  useEvents(GAME_EVENTS.READY_ON, () => {
    api.unfreeze(externalRef.current.uuid)
    setTarget('attack')
  })
  useCollision(() => {
    setTarget()
  }, externalRef.current?.uuid)
  useEffect(() => {
    if (outOfRing) {
      // setAttack(false)
      api.outOfThisWorld(externalRef.current.uuid)
      triggerOutOfRing(externalRef)
      animateFallUp(externalRef, () => {
        /* respawnMe(externalRef, api) */
        api.destroy(externalRef.current.uuid)
      })
    }
  }, [outOfRing])
  // const externalRef = useRef(null)
  const lookObject = new THREE.Object3D();

  function applyImpuse() {
    impulseRef.current = true
    api.impulse(externalRef.current.uuid, true)
    triggerImpulseOn({ uuid: externalRef.current.uuid, api })
  }
  function stopImpulse() {
    impulseRef.current = false
    api.impulse(externalRef.current.uuid, false)
    triggerImpulseOff({ uuid: externalRef.current.uuid, api })
  }
  function setTarget(forceTo) {
    const aggressiveness = getAggressivenessByModel(model)
    // const vagueness = model === 4 ? 0.1 : model === 3 ? 0.3 : model === 2 ? 0.5 : 0.8
    const wanderness = getWandernessByModel(model)
    
    if (Math.random() < aggressiveness || forceTo === 'attack') {
      // attack
      target.current = enemies[0].current
    } else if (Math.random() < wanderness || forceTo === 'wander') {
      // wander
      const range = (min, max) => Math.random() * (max - min) + min
      target.current = new THREE.Object3D()
      const pos = new THREE.Vector3( Math.round(range(5, -5)), 0, Math.round(range(5, -5)))
      target.current.position.copy(pos)
    } else {
      // wait
    }

    // refresh target after 3 secs
    clearTimeout(retargetTID.current)
    retargetTID.current = setTimeout(() => {
      setTarget()
    }, getReflexesByModel(model));
  }

  useFrame(() => {
    if (target.current) {
      const myself = externalRef.current
      const targetObj = target.current
      const impulseDuration = impulse.current
      // update helper position
      lookObject.position.copy(myself.position)
      // make helper to look at enemy (usually a player)
      lookObject.lookAt(targetObj.position)
      // dunno why I need to subtract 90 deg to Y rotation after lookAt
      lookObject.rotateY(-Math.PI / 2)

      if (lookObject.quaternion.angleTo(myself.quaternion) < 0.1 && !impulseRef.current) {
        applyImpuse()
        clearTimeout(impulseTID.current)
        impulseTID.current = setTimeout(stopImpulse, impulseDuration)
      }
    }
    
  })
  
  // how many times did a 360 spin
  useTurnsCounter(turns => {
    // counr?
  }, externalRef)

  let spinSpeed = 2
  switch (model) {
    case 1:
    default:
      spinSpeed = 2
      break
    case 2:
      spinSpeed = 1.5
      break
    case 3:
      spinSpeed = 2
      break
    case 4:
      spinSpeed = 2.5
      break
  }
  return useMemo(() =>
    <UfoShip
      ref={externalRef}
      model={model}
      {...props}
      userData={{
        origin: props.position,
        rotate: true,
        detached: false,
        isEnemy: true,
        freeze: true,
        spinFactor: spin === 'ccw' ? -1 : 1,
        spinSpeed,
        model,
      }} />, [])
})

export default Enemy
